import type { Agendamento, LinhaResultado } from './tipos.ts';
import { CARGOS } from './formato.ts';

/**
 * Exportação para planilha.
 *
 * CSV com ponto e vírgula e BOM de propósito: é o que o Excel em português
 * abre com as colunas já separadas e os acentos certos. Vírgula como separador
 * brigaria com a vírgula decimal, e sem o BOM o Excel lê UTF-8 como Latin-1 e
 * estraga todo "ção".
 */

const COLUNAS = [
  'Competência',
  'Nome',
  'Cargo',
  'Taxa Ocupação',
  'Fase Ocupação',
  'Premiação Ocupação',
  'Agendado',
  'Atendido',
  'Taxa Comparecimento',
  'Fase Comparecimento',
  'Premiação Comparecimento',
  'Volume Balizador',
  'Média Balizador',
  '% da Média',
  'Classificação Balizador',
  'Fator Balizador',
  'Soma',
  'Total Final',
] as const;

/** Número no formato que o Excel pt-BR entende: vírgula decimal, sem separador de milhar. */
function decimal(valor: number | null | undefined, casas = 2): string {
  if (valor === null || valor === undefined) return '';
  return valor.toFixed(casas).replace('.', ',');
}

function campo(valor: string): string {
  // Ponto e vírgula, aspas ou quebra de linha dentro do valor exigem aspas.
  return /[;"\n\r]/.test(valor) ? `"${valor.replace(/"/g, '""')}"` : valor;
}

export function csvDoFechamento(competencia: string, linhas: LinhaResultado[]): string {
  const cabecalho = COLUNAS.join(';');

  const corpo = linhas.map((l) =>
    [
      competencia,
      l.nome,
      CARGOS[l.cargoCodigo] ?? l.cargoCodigo,
      decimal(l.ocupacaoTaxa * 100),
      l.ocupacaoFase,
      decimal(l.ocupacaoValor),
      decimal(l.compAgendado),
      decimal(l.compAtendido),
      decimal(l.compTaxa * 100),
      l.compFase,
      decimal(l.compValor),
      decimal(l.balizVolume),
      decimal(l.balizMedia),
      decimal(l.balizPercentual === null ? null : l.balizPercentual * 100),
      l.balizRotulo,
      decimal(l.balizFator * 100),
      decimal(l.somaPremiacao),
      decimal(l.totalFinal),
    ]
      .map((v) => campo(String(v)))
      .join(';'),
  );

  const total = [
    '',
    'TOTAL GERAL',
    ...Array<string>(COLUNAS.length - 3).fill(''),
    decimal(linhas.reduce((s, l) => s + l.totalFinal, 0)),
  ].join(';');

  return [cabecalho, ...corpo, total].join('\r\n');
}

/** Dispara o download no navegador. O BOM vai na frente para o Excel acertar o acento. */
export function baixar(nomeArquivo: string, conteudo: string): void {
  const blob = new Blob(['﻿', conteudo], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = nomeArquivo;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

const COLUNAS_AGENDAMENTOS = [
  'Prontuário',
  'Paciente',
  'Unidade',
  'Data',
  'Criação do agendamento',
  'Data do Primeiro Atendimento',
  'Data do Último Atendimento',
  'Procedimento',
  'Usuário',
  'Status Anterior',
  'Status',
] as const;

/**
 * A base de agendamentos em planilha, nas mesmas colunas do relatório que antes
 * era colado à mão. Exporta o que está filtrado, não o que está visível na
 * tela — a tela mostra 200 linhas por desempenho, e ninguém espera que a
 * exportação venha cortada.
 */
export function csvDosAgendamentos(linhas: Agendamento[]): string {
  const corpo = linhas.map((a) =>
    [
      a.prontuario ?? '',
      a.paciente ?? '',
      a.unidade ?? '',
      a.data ?? '',
      a.criacao ?? '',
      a.primeiroAtendimento ?? '',
      a.ultimoAtendimento ?? '',
      a.procedimento,
      a.usuario,
      a.statusAnterior ?? '',
      a.status,
    ]
      .map((v) => campo(String(v)))
      .join(';'),
  );

  return [COLUNAS_AGENDAMENTOS.join(';'), ...corpo].join('\r\n');
}
